/**
 * When they want it done — one control everywhere (`docs/new-request.md`
 * §8.1, Aaron's comment 6, T25).
 *
 * Ten chips on every board and every width: *ASAP · This week · Next 2 weeks
 * · This month · Next month · <three seasons> · Pick a date · Not sure*, and
 * *Pick a date* adds *By · On · After · Between*. The sheet draws
 * `whenChips(today)`; the server recomputes `wantedWindow()` on save, so the
 * stored window is the server's and never the browser's clock.
 */
import { addDays, daysInMonth, monthOf, parseDay, formatDay, compareDays } from '../calendar.js';
import { dayComma, monthShort, weekdayOf } from './words.js';
export const WANTED_KINDS = [
    'asap', 'this_week', 'two_weeks', 'this_month', 'next_month',
    'season', 'by', 'on', 'after', 'between', 'not_sure',
];
/** The four ways *Pick a date* reads a date, *By* first. */
export const PICK_MODES = ['by', 'on', 'after', 'between'];
export const PICK_MODE_LABELS = {
    by: 'By', on: 'On', after: 'After', between: 'Between',
};
export const SEASONS = ['winter', 'spring', 'summer', 'fall'];
const SEASON_WORD = {
    winter: 'Winter', spring: 'Spring', summer: 'Summer', fall: 'Fall',
};
/** The month each season starts in: Winter Dec 1, Spring Mar 1, Summer Jun 1, Fall Sep 1. */
const SEASON_START = { winter: 12, spring: 3, summer: 6, fall: 9 };
export const seasonKeyText = (key) => `${key.season}-${key.year}`;
export function parseSeasonKey(text) {
    const match = /^(winter|spring|summer|fall)-(\d{4})$/.exec(text);
    return match ? { season: match[1], year: Number(match[2]) } : null;
}
/** The season a day is in. January's winter is the one that began the December before. */
function seasonOf(day) {
    const { year, month } = parseDay(day);
    if (month === 12)
        return { season: 'winter', year };
    if (month <= 2)
        return { season: 'winter', year: year - 1 };
    if (month <= 5)
        return { season: 'spring', year };
    if (month <= 8)
        return { season: 'summer', year };
    return { season: 'fall', year };
}
function nextSeason(key) {
    const order = ['winter', 'spring', 'summer', 'fall'];
    const next = order[(order.indexOf(key.season) + 1) % 4];
    /* Winter starts in December of its own year; the spring after it is the next year's. */
    return { season: next, year: key.season === 'winter' ? key.year + 1 : key.year };
}
/** The first and last day of a season. */
export function seasonWindow(key) {
    const startMonth = SEASON_START[key.season];
    const start = formatDay({ year: key.year, month: startMonth, day: 1 });
    const endYear = key.season === 'winter' ? key.year + 1 : key.year;
    const endMonth = key.season === 'winter' ? 2 : startMonth + 2;
    const end = formatDay({ year: endYear, month: endMonth, day: daysInMonth(endYear, endMonth) });
    return { start, end };
}
/** The next three seasons after the one `today` is in. */
export function nextSeasons(today) {
    const first = nextSeason(seasonOf(today));
    const second = nextSeason(first);
    return [first, second, nextSeason(second)];
}
/** *This winter* for the first of the three, *Spring 2027* for the others. */
export function seasonLabelFor(key, today) {
    const [first] = nextSeasons(today);
    if (first && first.season === key.season && first.year === key.year) {
        return `This ${key.season}`;
    }
    return `${SEASON_WORD[key.season]} ${key.year}`;
}
/** The ten chips, in order (Board 6). */
export function whenChips(today) {
    return [
        { key: 'asap', label: 'ASAP' },
        { key: 'this_week', label: 'This week' },
        { key: 'two_weeks', label: 'Next 2 weeks' },
        { key: 'this_month', label: 'This month' },
        { key: 'next_month', label: 'Next month' },
        ...nextSeasons(today).map((key) => ({
            key: `season:${seasonKeyText(key)}`, label: seasonLabelFor(key, today),
        })),
        { key: 'pick', label: 'Pick a date' },
        { key: 'not_sure', label: 'Not sure' },
    ];
}
/** The coming Sunday, or today when today is one. */
function comingSunday(today) {
    const weekday = weekdayOf(today);
    return addDays(today, weekday === 0 ? 0 : 7 - weekday);
}
/**
 * The window a choice means, from `today` in the branch's time zone (§8.1).
 * A Between whose second date is before the first swaps them (§19 B16).
 */
export function wantedWindow(choice, today) {
    const window = (start, end, text, label = null) => ({ kind: choice.kind, start, end, label, text });
    switch (choice.kind) {
        case 'asap': return window(today, null, 'ASAP');
        case 'this_week': return window(today, comingSunday(today), 'this week');
        case 'two_weeks': return window(today, addDays(today, 14), 'within 2 weeks');
        case 'this_month': return window(today, monthOf(today).end, 'this month');
        case 'next_month': {
            const range = monthOf(addDays(monthOf(today).end, 1));
            return window(range.start, range.end, `next month (${monthShort(range.start)})`);
        }
        case 'season': {
            const key = parseSeasonKey(choice.season ?? '') ?? nextSeasons(today)[0];
            const range = seasonWindow(key);
            const label = seasonLabelFor(key, today);
            return window(range.start, range.end, label, label);
        }
        case 'by': {
            const date = requireDate(choice.date);
            const start = compareDays(today, date) <= 0 ? today : date;
            return window(start, date, `by ${dayComma(date)}`);
        }
        case 'on': {
            const date = requireDate(choice.date);
            return window(date, date, `on ${dayComma(date)}`);
        }
        case 'after': {
            const date = requireDate(choice.date);
            return window(date, null, `after ${dayComma(date)}`);
        }
        case 'between': {
            let first = requireDate(choice.date);
            let second = requireDate(choice.date2);
            if (compareDays(second, first) < 0)
                [first, second] = [second, first];
            return window(first, second, `${dayComma(first)} – ${dayComma(second)}`);
        }
        case 'not_sure': return window(null, null, 'not sure');
    }
}
function requireDate(value) {
    if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        throw new Error('This choice needs a date.');
    }
    return value;
}
/** The words for a window already stored on a job (`wanted_*`), without today. */
export function wantedText(stored) {
    switch (stored.kind) {
        case null:
        case undefined: return null;
        case 'asap': return 'ASAP';
        case 'this_week': return 'this week';
        case 'two_weeks': return 'within 2 weeks';
        case 'this_month': return 'this month';
        case 'next_month': return stored.start ? `next month (${monthShort(stored.start)})` : 'next month';
        case 'season': return stored.label;
        case 'by': return stored.end ? `by ${dayComma(stored.end)}` : null;
        case 'on': return stored.start ? `on ${dayComma(stored.start)}` : null;
        case 'after': return stored.start ? `after ${dayComma(stored.start)}` : null;
        case 'between':
            return stored.start && stored.end ? `${dayComma(stored.start)} – ${dayComma(stored.end)}` : null;
        case 'not_sure': return 'not sure';
        default: return null;
    }
}
/**
 * *about 5 weeks from today*; *today*, *tomorrow* and *in n days* under two
 * weeks (§8.1). A date already gone says so, and is still allowed (§19 B16).
 */
export function fromToday(date, today) {
    const days = Math.round((Date.parse(`${date}T12:00:00Z`) - Date.parse(`${today}T12:00:00Z`)) / 86_400_000);
    if (days < 0)
        return 'that’s in the past';
    if (days === 0)
        return 'today';
    if (days === 1)
        return 'tomorrow';
    if (days < 14)
        return `in ${days} days`;
    return `about ${Math.round(days / 7)} weeks from today`;
}
//# sourceMappingURL=when.js.map